import {
  Button,
  FormItem,
  ListGroup,
  Modal,
  Overlay,
  TextArea,
} from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useController, useForm } from 'react-hook-form';

import translations from 'src/lang';

import { useRenameStanleyQueryHistoryItem } from '../../../../../../../../../../../api/hooks';

const tRenameConfirm =
  translations['en-US'].STANLEY_QUERY.MODAL.HISTORY.RENAME_CONFIRM;

interface RenameFormValues {
  name: string;
}

interface StanleyQueryHistoryItemRenameModalProps {
  isOpen: boolean;
  runId: string;
  title: string;
  onClose: () => void;
}

export const StanleyQueryHistoryItemRenameModal: FC<
  StanleyQueryHistoryItemRenameModalProps
> = ({ isOpen, runId, title, onClose }) => {
  const { mutate: renameItem, isLoading: isRenaming } =
    useRenameStanleyQueryHistoryItem();

  const { control, handleSubmit, reset } = useForm<RenameFormValues>({
    defaultValues: { name: title },
  });

  const { field: nameField } = useController({ name: 'name', control });

  useEffect(() => {
    if (isOpen) reset({ name: title });
  }, [isOpen, title, reset]);

  const onRenameSubmit = useCallback(
    (values: RenameFormValues) => {
      const trimmed = values.name.trim();
      if (!trimmed || trimmed === title) {
        onClose();
        return;
      }
      renameItem({ run_id: runId, title: trimmed }, { onSuccess: onClose });
    },
    [renameItem, runId, title, onClose]
  );

  const isRenameDisabled =
    isRenaming || !nameField.value.trim() || nameField.value.trim() === title;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();

        if (!isRenameDisabled) handleSubmit(onRenameSubmit)();
      }
    },
    [isRenameDisabled, handleSubmit, onRenameSubmit]
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    <Overlay isOpen={isOpen}>
      <Modal
        data-test-id="stanley-history-rename-confirm-modal"
        closable
        handleClose={onClose}
        title={tRenameConfirm.TITLE}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Button
                key="confirm-rename"
                size="small"
                variant="primary"
                disabled={isRenameDisabled}
                onClick={handleSubmit(onRenameSubmit)}
              >
                {tRenameConfirm.CONFIRM}
              </Button>,
              <Button
                key="cancel-rename"
                size="small"
                variant="outlined"
                disabled={isRenaming}
                onClick={onClose}
              >
                {tRenameConfirm.CLOSE}
              </Button>,
            ]}
          />
        }
      >
        <FormItem label={tRenameConfirm.LABEL} className="h-full mb-0">
          <TextArea
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            onKeyDown={handleKeyDown}
            style={{ height: '150px' }}
          />
        </FormItem>
      </Modal>
    </Overlay>,
    document.body
  );
};
